import React, {useEffect, useRef} from "react";
import {css} from "aphrodite";
import styles from "./styles";
import {Tooltip, IconButton, Typography} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {useSelector} from "react-redux";
import Appointment from "../../../Models/Appointment/Appointment";
import {Formik, Field} from "formik";
import * as Yup from "yup";
import CustomInput from "../../common/components/CustomInput/CustomInput";
import {stepLayoutItemsId, validatePhoneNumber} from "../../../Utils";
import {useTranslation} from "react-i18next";
import {CommonLayout} from "../../common/components/CommonLayout";
import {StepInfo} from "../../common/components/StepInfo";
import {CustomMainButton} from "../../common/components/CustomMainButton";
import {useNavigate} from "react-router";
import {RoutePath} from "../../../Utils/routePath";
import {RequestLayout} from "../../common/components/RequestLayout";
import {Media} from "../../../Models";
import OtpModal from "../../common/components/OTPDialog/OTPModal";
import FriendlyCaptcha from "../../common/components/FriendlyCaptcha/FriendlyCaptcha";
import PhoneNumberInputSelect from "../../common/components/PhoneNumberInputSelect.js/PhoneNumberInputSelect";
import i18n from "../../../i18n";
import Logger from "../../../Utils/Logger";
import DynamicFieldsRenderer from "../../common/components/DynamicFieldsRenderer";
import {validateDynamicField, areAllDynamicFieldsValid} from "../../../Utils/dynamicFieldValidation";

const VerifyContactDetailsPage = () => {
  const [t] = useTranslation(["translation"]);
  const [tt] = useTranslation(["tags"]);
  const navigate = useNavigate();
  const currentStep = stepLayoutItemsId.verifyContactDetails;
  const stepItems = useSelector(Appointment.getStepItems);
  const appointmentState = useSelector(Appointment.getAppointmentState);
  const webView = useSelector(Media.getIsWebView);
  const bookCode = useSelector(Appointment.getBookCode);
  const loading = useSelector(Appointment.getAppointmentIsLoading);
  const error = useSelector(Appointment.getAppointmentError);
  const mfaVerificationToken = useSelector(Appointment.getMfaVerificationToken);
  const loadingMfa = useSelector(Appointment.getMfaIsLoading);
  const errorMfa = useSelector(Appointment.getMfaError);
  const selectedService = useSelector(Appointment.getSelectedService);
  const [captcha, setCaptcha] = React.useState(null);
  const [otpModalVisible, setOtpModalVisible] = React.useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = React.useState(false);
  const friendlyCaptchaRef = React.useRef(null);
  const [localUserData, setLocalUserData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    tin: "",
    phoneNumber: "",
    notes: "",
  });
  const [extraFields, setExtraFields] = React.useState([]);
  const [pendingResendOtp, setPendingResendOtp] = React.useState(0);
  const lastCaptcha = useRef(null);
  const selectedServiceId = useSelector(Appointment.getSelectedServiceId);
  const selectedBranchId = useSelector(Appointment.getSelectedBranchId);
  const selectedSubServiceGroupId = useSelector(Appointment.getSelectedSubServiceGroupId);
  const selectedSlotDataVisibility = useSelector(Appointment.getSelectedSlotDataVisibility);
  const isEditAppointmentMode = useSelector(Appointment.getIsEditAppointmentMode);
  const isAppointmentByBranch = useSelector(Appointment.getIsAppointmentByBranch);
  
  // Dynamic Fields State
  const [dynamicFieldValues, setDynamicFieldValues] = React.useState({});
  const [dynamicFieldTouched, setDynamicFieldTouched] = React.useState({});
  const [dynamicFieldErrors, setDynamicFieldErrors] = React.useState({});

  // Boolean (Bit) fields default to "false" (No) so untouched switches are still submitted
  useEffect(() => {
    const fields = selectedService?.dynamicFields;
    if (!Array.isArray(fields) || fields.length === 0) {
      return;
    }

    setDynamicFieldValues((prev) => {
      const next = { ...prev };
      let changed = false;

      fields.forEach((field) => {
        if (field.type !== 3) {
          return;
        }
        const fieldId = field.dynamicFieldId;
        if (next[fieldId] === undefined || next[fieldId] === null || next[fieldId] === '') {
          next[fieldId] = 'false';
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [selectedService?.dynamicFields]);

  useEffect(() => {
    if (isEditAppointmentMode) return; 

    if (!selectedServiceId || !selectedBranchId || !selectedSlotDataVisibility) {
      if (isAppointmentByBranch) {
        if (!selectedBranchId) {
          navigate(RoutePath.selectBranchPage, { replace: true });
        } else if (!selectedServiceId) {
          if (selectedSubServiceGroupId) {
            navigate(RoutePath.selectSubServicePage, { replace: true });
          } else {
            navigate(RoutePath.selectServicePage, { replace: true });
          }
        } else if (!selectedSlotDataVisibility) {
          navigate(RoutePath.selectDateAndTimePage, { replace: true });
        }
      } else {
        if (!selectedServiceId) {
          if (selectedSubServiceGroupId) {
            navigate(RoutePath.selectSubServicePage, { replace: true });
          } else {
            navigate(RoutePath.selectServicePage, { replace: true });
          }
        } else if (!selectedBranchId) {
          navigate(RoutePath.selectBranchPage, { replace: true });
        } else if (!selectedSlotDataVisibility) {
          navigate(RoutePath.selectDateAndTimePage, { replace: true });
        }
      }
    }
  }, [selectedServiceId, selectedBranchId, selectedSubServiceGroupId, selectedSlotDataVisibility, isEditAppointmentMode, isAppointmentByBranch, navigate]);

  useEffect(() => {
    if (lastCaptcha.current !== captcha) {
      lastCaptcha.current = captcha;
      if (pendingResendOtp && captcha ) {
        handleResendOTP()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captcha, pendingResendOtp]);

  useEffect(() => {
    if (mfaVerificationToken) {
      setOtpModalVisible(true);
    }
  }, [mfaVerificationToken, navigate]);

  useEffect(() => {
    return () => {
      Appointment.clearMfaData();
    };
  }, []);

  useEffect(() => {
    if (bookCode) {
      navigate(RoutePath.appointmentCompletedPage);
    }
  }, [bookCode, navigate]);

  useEffect(() => {
    if (errorMfa || error) {
      setCaptcha(null);
      friendlyCaptchaRef.current?.reset();
    }
  }, [errorMfa, error]);

  const handleCaptchaSuccess = (solution) => {
    setCaptcha(solution);
  }
  const handleCaptchaFailure = (e) => {
    setCaptcha(null);
  }
  const handleCaptchaExpired = (e) => {
    Logger.log(e, "CAPTCHA expired: ");
  }

  const nameRegex = /^[a-zA-Zα-ωΑ-Ωά-ώίϊΐόύΰήέΆ-Ώ\s.\-']+$/;
  const tinRegex = /^[a-zA-Z0-9\s.\-/+*]*$/;
  const restrictedCharsRegex = /[<>`|~^]/;

  const validationSchema = Yup.object().shape({
   firstName: Yup.string()
        .required(t("Name is required"))
        .min(1, t("Name is too short")) // OWASP recommendation: Define min length
        .max(100, t("Max 100 characters"))
        .test('is-valid-name', t('Name is invalid'), (value) => nameRegex.test(value)),        
    lastName: Yup.string()
        .required(t("Last name is required"))
        .min(1, t("Last name is too short"))
        .max(100, t("Max 100 characters"))
        .test('is-valid-last-name', t('Last Name is invalid'), (value) => nameRegex.test(value)),        
    tin: Yup.string()
        .required(t("TIN is required"))
        .min(1, t("TIN is too short"))
        .max(50, t("Max 50 characters"))
        .test('is-valid-tin', t('TIN is invalid'), (value) => tinRegex.test(value)),
    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Email is required")),
    phoneNumber: Yup.string()
      .test(
        "is-valid-phone-number",
        t("Phone number is not valid"),
        validatePhoneNumber
      )
      .required(t("Phone number is required")),
    notes: Yup.string()
      .required(t("Notes is required"))
      .min(1, t("Notes are too short"))
      .max(1000, t("Notes are too long"))
      .test('no-restricted-chars', t("Characters < > ` | ~ ^ are not allowed"), (value) => {
        if (!value) return true;
        return !restrictedCharsRegex.test(value);
      })
      .trim(),
  });

  const handleSendMfa = (informationValues) => {
    Appointment.requestMfaSend({
      Email: informationValues?.email,
      PhoneNumber: informationValues?.phoneNumber,
      Purpose: 0,
      CaptchaResponse: captcha,
    }); 
  };

  const onSubmitForm = (values) => {
    const normalizedValues = {
      ...values,
      firstName: values.firstName.normalize('NFC'),
      lastName: values.lastName.normalize('NFC'),
      notes: values.notes.normalize('NFC'),
      tin: values.tin.normalize('NFC'),
    };
    Appointment.setUserInformation(normalizedValues);
    setLocalUserData(normalizedValues);
    handleSendMfa(normalizedValues);
  };
  const handleExtraFieldChange = (index, field, value) => {
    setExtraFields(prevExtraFields => {
      const newExtraFields = [...prevExtraFields];
      newExtraFields[index] = value;
      return newExtraFields;
    })
  }
  
  // Dynamic Fields Handlers
  const handleDynamicFieldChange = (fieldId, value, field) => {
    setDynamicFieldValues(prev => ({ ...prev, [fieldId]: value }));
    
    // Validate immediately on change
    const error = validateDynamicField(field, value, t);
    setDynamicFieldErrors(prev => ({ ...prev, [fieldId]: error }));
  };

  const handleDynamicFieldBlur = (fieldId, value, field) => {
    setDynamicFieldTouched(prev => ({ ...prev, [fieldId]: true }));
    
    // Validate on blur
    const error = validateDynamicField(field, value, t);
    setDynamicFieldErrors(prev => ({ ...prev, [fieldId]: error }));
  };
  const handleReturnToHome = () => {
    navigate(RoutePath.home);
  };

  const handleCloseOtpModal = () => {
    setOtpModalVisible(false);
    setCaptcha(null);
    friendlyCaptchaRef.current?.reset();
    Appointment.clearMfaData();
  };

  const handleResendOTP = () => {
    setPendingResendOtp(0);
    handleSendMfa(localUserData);
  };
  const triggerResendOtp = () => {
    setPendingResendOtp(Date.now());
    friendlyCaptchaRef.current?.reset();
  }

  const handelSubmitOtp = (otp) => {
    const extraFieldKeys = selectedService?.tags?.ExtraFields?.split('|') || [];    
    const extraFieldsDescription = extraFields.map((ef, i) => 
      ef ? `${extraFieldKeys[i]}: ${ef.normalize('NFC')}` : null
    )
    .filter(ef => !!ef).join(', ');
    
    const notesDescription = localUserData?.notes ? `Notes: ${localUserData.notes.normalize('NFC')}` : '';    
    const descriptionParts = [notesDescription, extraFieldsDescription].filter(part => part !== '');
    const description = descriptionParts.join(', ');
    
    // Prepare dynamic fields for submission
    // Boolean fields default to "false" when never touched (switch shows No)
    const dynamicFieldsArray = selectedService?.dynamicFields?.map((field) => {
      const rawValue = dynamicFieldValues[field.dynamicFieldId];
      const value = field.type === 3
        ? (rawValue === 'true' || rawValue === 'false' ? rawValue : 'false')
        : (rawValue || '');

      return {
        dynamicFieldId: field.dynamicFieldId,
        value,
      };
    }).filter((df) => df.value !== '') || [];
    
    const appointmentData = {
      Start: appointmentState.selectedAvailableSlot?.start,
      Channel: appointmentState.selectedChannel,
      BranchId: appointmentState.selectedBranchId,
      Description: description || '',
      NewGuests: [
        {
          firstName: localUserData?.firstName,
          lastName: localUserData?.lastName,
          phoneNumber: localUserData?.phoneNumber,
          email: localUserData?.email,
          vatNumber: localUserData?.tin,
          language: i18n.language === 'gr' ? 'el-GR' : 'en-US'
        },
      ],
      Validations: [
        {
          Token: otp,
          VerificationToken: mfaVerificationToken,
        },
      ],
      DynamicFields: dynamicFieldsArray.length > 0 ? dynamicFieldsArray : undefined,
    };
    
    Appointment.requestBookAppointment(
      appointmentState.selectedServiceId,
      appointmentData
    );
  }

  return (
    <Formik
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        tin: "",
        phoneNumber: "",
        notes: "",
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmitForm}
    >
      {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          isValid,
          errors,
          dirty,
        }) => (
        <CommonLayout>
          <StepInfo
            activeStepId={currentStep}
            layoutItems={stepItems}
            webView={webView}
          />
          <RequestLayout
            loading={loadingMfa && !otpModalVisible}
            error={error || errorMfa}
            overlayLoading
          >
            <div className={css(styles.centerContainer)}>
              <Field
                id="firstName"
                label={t("Name")}
                type="text"
                name="firstName"
                placeholder={t("Name") + " *"}
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                component={CustomInput}
              />
              <Field
                id="lastName"
                label={t("Last Name")}
                type="text"
                name="lastName"
                placeholder={t("Last Name") + " *"}
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                component={CustomInput}
              />
              <Field
                id="tin"
                label={t("TIN")}
                type="text"
                name="tin"
                placeholder={t("TIN") + " *"}
                value={values.tin}
                onChange={handleChange}
                onBlur={handleBlur}
                component={CustomInput}
              />
              <Field
                id="email"
                label={t("E-mail")}
                type="text"
                name="email"
                placeholder="name@domain.com *"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                component={CustomInput}
              />
              <div className={css(styles.phoneNumberContainer)}>
                <div className={css(styles.phoneNumberInnerContainer)}>
                  <Field
                      id="phoneNumber"
                      label={t("Mobile Phone")}
                      type="text"
                      name="phoneNumber"
                      placeholder={"69xxxxxxxx *"}
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      component={PhoneNumberInputSelect}
                  />
                </div>
              </div>
              <div className={css(styles.textareaContainer)}>
                <div className={css(styles.textareaLabelContainer)}>
                  <Typography
                    variant="body1"
                    component="label"
                    htmlFor="notes"
                  >
                    {t("Notes")} *
                  </Typography>
                  {webView ? (
                    <Tooltip title={t("Notes info")} placement="bottom" arrow>
                      <IconButton size="small" className={css(styles.infoIconButton)} aria-label={t("Notes info")}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      
                      <IconButton size="small" className={css(styles.infoIconButton)} onClick={() => setMobileInfoOpen(!mobileInfoOpen)} aria-label={t("Notes info")}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </div>
                {!webView && mobileInfoOpen && (
                  <Typography variant="caption" className={css(styles.mobileInfoText)} id="notes-info">
                    {t("Notes info")}
                  </Typography>
                )}
                <textarea
                  id="notes"
                  name="notes"
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={Boolean(touched.notes && errors.notes)}
                  aria-describedby={[
                    touched.notes && errors.notes ? "notes-error" : null,
                    !webView && mobileInfoOpen ? "notes-info" : null,
                  ].filter(Boolean).join(" ") || undefined}
                  className={css(styles.textareaField, touched.notes && errors.notes && styles.textareaError)}
                />
                {touched.notes && errors.notes && (
                  <div className={css(styles.errorField)} id="notes-error" role="alert">
                    <Typography className={css(styles.error)} variant="subtitle2">
                      {errors.notes}
                    </Typography>
                  </div>
                )}
              </div>
              {selectedService?.tags?.ExtraFields?.split('|').map(field => tt(field)).map((field, index) => (
                  <Field
                      placeholder={field}
                      name={field}
                      key={index}
                      sx={{ width: '100%', maxWidth: 400 }}
                      label={field}
                      value={extraFields[index] || ''}
                      onChange={(event) => handleExtraFieldChange(index, field, event.target.value)}
                      component={CustomInput}
                  />
              ))}
              {
                selectedService?.dynamicFields?.length > 0 && (
                  <h4 className={css(styles.textareaContainer)}>
                    Extra Fields
                  </h4>
                )
              }
              <DynamicFieldsRenderer
                dynamicFields={selectedService?.dynamicFields || []}
                values={dynamicFieldValues}
                touched={dynamicFieldTouched}
                errors={dynamicFieldErrors}
                onChange={handleDynamicFieldChange}
                onBlur={handleDynamicFieldBlur}
              />
              <div className={css(styles.captchaContainer)}>
                <FriendlyCaptcha
                  style={{width: '100%', maxWidth: 400}}
                  ref={friendlyCaptchaRef}
                  onSuccess={handleCaptchaSuccess}
                  onFailure={handleCaptchaFailure}
                  onExpired={handleCaptchaExpired}
                />
              </div>
              <CustomMainButton
                isDisabled={
                  isValid && 
                  dirty && 
                  !!captcha && 
                  areAllDynamicFieldsValid(selectedService?.dynamicFields || [], dynamicFieldValues, t) 
                    ? false 
                    : true
                }
                fixedSize={false}
                value={t("Book Appointment")}
                onClick={handleSubmit}
                style={styles.button}
              />
            </div>
          </RequestLayout>
          <OtpModal
            isLoading={loading || loadingMfa}
            visible={otpModalVisible}
            onValidateOtp={handelSubmitOtp}
            onClose={handleCloseOtpModal}
            handleReturnToHome={handleReturnToHome}
            handleResendOtp={triggerResendOtp}
            validateButtonText={t("Confirm")}
            phoneOrEmail={localUserData?.phoneNumber}
            maxTries={4}
            timer={120}
            error={errorMfa || error}
          />
        </CommonLayout>
      )}
    </Formik>
  );
};

export {VerifyContactDetailsPage};
